import * as React from 'react';
import { bus } from '@pkvsinha/react-integrate';

const NavigationContext = React.createContext(undefined);

class NavigationStore {
    constructor(initial = "/") {
        this.listeners = new Set();
        this._location = this.parse(initial);
        bus.subscribe((cmd) => this.onCommand(cmd));
    }
    reset(path = "/") {
        this._location = this.parse(path);
    }
    parse(url) {
        const parsed = new URL(url, "http://dummy");
        return {
            path: parsed.pathname,
            query: Object.fromEntries(parsed.searchParams.entries()),
            hash: parsed.hash ? parsed.hash.slice(1) : null,
        };
    }
    get location() {
        return this._location;
    }
    subscribe(listener) {
        this.listeners.add(listener);
        listener(this._location); // fire immediately
        return () => this.listeners.delete(listener);
    }
    // navigate(to: string) {
    //   this._location = this.parse(to);
    //   for (const l of this.listeners) l(this._location);
    // }
    // INTERNAL: called from bus
    onCommand(cmd) {
        console.log("NavigationStore: received command", cmd);
        switch (cmd.type) {
            case "navigate":
            case "replace":
                if (typeof cmd.target === "string") {
                    this._location = this.parse(cmd.target);
                    this.emit();
                }
                break;
        }
    }
    emit() {
        console.log("NavigationStore: emitting command", this._location);
        for (const l of this.listeners)
            l(this._location);
    }
}
const navigationStore = new NavigationStore("/");
function attachBrowserAdapter(contextPath = "/") {
    console.log("reseting the navigation store to account for current location: ", contextPath);
    navigationStore.reset(contextPath); // need to see if we need to reset the listeners
    const onPop = () => {
        // navigationStore.navigate(window.location.pathname + window.location.search + window.location.hash);
        bus.send({
            type: "navigate",
            target: window.location.pathname +
                window.location.search +
                window.location.hash,
        });
    };
    // Sync from browser → store
    window.addEventListener("popstate", onPop);
    // Sync from store → browser
    const unsub = navigationStore.subscribe((loc) => {
        const url = loc.path +
            (Object.keys(loc.query).length
                ? "?" + new URLSearchParams(loc.query).toString()
                : "") +
            (loc.hash ? "#" + loc.hash : "");
        window.history.pushState({}, "", url);
    });
    return () => {
        window.removeEventListener("popstate", onPop);
        unsub();
    };
}
function attachMemoryAdapter(start = "/") {
    // navigationStore.navigate(start);
    bus.send({ type: "replace", target: start });
    return () => { }; // no-op cleanup
}

const NavigationProvider = ({ children }) => {
    const [location, setLocation] = React.useState(navigationStore.location);
    const [activeComponents, setActiveComponents] = React.useState([]);
    React.useEffect(() => {
        const unsubNav = navigationStore.subscribe(setLocation);
        const unsubCmd = bus.subscribe(cmd => {
            console.log("NavigationProvider: received command", cmd);
            if (cmd.type === "open" && cmd.target) {
                setActiveComponents(prev => [...new Set([...prev, cmd.target])]);
            }
            else if (cmd.type === "close" && cmd.target) {
                setActiveComponents(prev => prev.filter(c => c !== cmd.target));
            }
            else if (cmd.type === "navigate" && typeof cmd.payload === "string") {
                // navigationStore.navigate(cmd.payload);
                console.log("NavigationProvider: navigate to", cmd.payload);
            }
        });
        return () => {
            unsubNav();
            unsubCmd();
        };
    }, []);
    // React.useEffect(() => {
    //     const unsub = navigationStore.subscribe(setLocation);
    //     return () => {
    //         unsub();
    //     }
    // }, []);
    const send = React.useCallback((cmd) => {
        console.log("NavigationProvider: sending navigation command", cmd);
        return bus.send(cmd);
    }, []);
    return (React.createElement(NavigationContext, { value: { location, send /* navigate: navigationStore.navigate.bind(navigationStore) */ } }, children));
};

// export function useNavigation() {
//     const context = React.useContext(NavigationContext);
//     if (context === undefined) {
//         throw new Error("useNavigation must be used within a NavigationContext or Navigation Provider: " + context);
//     }
//     return { navigate: context.navigate }
// }
function useNavigation() {
    const context = React.useContext(NavigationContext);
    if (context === undefined) {
        throw new Error("useNavigation must be used within a NavigationContext or Navigation Provider: " + context);
    }
    const navigate = (to) => context.send({ type: "navigate", target: to });
    const replace = (to) => context.send({ type: "replace", target: to });
    const back = () => context.send({ type: "back" });
    const forward = () => context.send({ type: "forward" });
    const send = (cmd) => context.send(cmd);
    return { location: context.location, navigate, replace, back, forward, send };
}

function Navigate(props) {
    const { navigate, replace: doReplace } = useNavigation();
    const go = () => (props.replace ? doReplace(props.to) : navigate(props.to));
    const handleClick = (event) => {
        // Prevent the browser's default link behavior (full page refresh)
        event.preventDefault();
        // Use our custom navigate function
        go();
    };
    return React.createElement("a", { href: props.to, className: props.className, onClick: handleClick }, props.label || props.children || "Navigate");
}

function matchRoute(url, routes) {
    const parsed = new URL(url, "https://prashantsinha.com"); // dummy base for relative paths
    const pathname = parsed.pathname;
    const query = Object.fromEntries(parsed.searchParams.entries());
    const hash = parsed.hash ? parsed.hash.slice(1) : null;
    for (const [pattern, Component] of Object.entries(routes)) {
        const params = matchPattern(pattern, pathname);
        if (params) {
            return { Component, params, query, hash };
        }
    }
    return null;
}
function matchPattern(pattern, pathname) {
    const patternParts = pattern.split("/").filter(Boolean);
    const pathParts = pathname.split("/").filter(Boolean);
    // Wildcard handling
    if (patternParts[patternParts.length - 1] === "*") {
        if (pathParts.length < patternParts.length - 1)
            return null;
    }
    else if (patternParts.length !== pathParts.length) {
        return null;
    }
    const params = {};
    for (let i = 0; i < patternParts.length; i++) {
        const p = patternParts[i];
        const v = pathParts[i];
        if (p === "*") {
            params["*"] = pathParts.slice(i).join("/");
            return params;
        }
        else if (p.startsWith(":")) {
            params[p.slice(1)] = v;
        }
        else if (p !== v) {
            return null;
        }
    }
    return params;
}

// Create a context with a default null value
const RoutingContext = React.createContext(null);
// Export the provider to be used in your Router
const RoutingProvider = RoutingContext.Provider;

const useCurrentPath = () => {
    const context = React.useContext(NavigationContext);
    if (!context) {
        throw new Error("Router must be used within a NavigationContext");
    }
    return context.location;
};
const Router = ({ routes, x404 }) => {
    var _a;
    // Router implementation goes here
    const location = useCurrentPath();
    const match = matchRoute(location.path, routes);
    const routeParams = {
        path: location.path,
        params: (match === null || match === void 0 ? void 0 : match.params) || {},
        query: (match === null || match === void 0 ? void 0 : match.query) || {},
        hash: (match === null || match === void 0 ? void 0 : match.hash) || "",
    };
    const FallbackComponent = routes["home"] || x404;
    const Component = (_a = match === null || match === void 0 ? void 0 : match.Component) !== null && _a !== void 0 ? _a : FallbackComponent;
    return React.createElement(RoutingProvider, { value: routeParams },
        React.createElement(Component, null));
};

/**
 * A custom hook to access route parameters (params, query, hash).
 * Throws an error if used outside of a RouterContext.Provider.
 */
function useRouteParams() {
    const context = React.useContext(RoutingContext);
    if (!context) {
        throw new Error('useRouteParams must be used within a RoutingContext or Router component.');
    }
    return context;
}

export { Navigate, NavigationContext, NavigationProvider, Router, attachBrowserAdapter, attachMemoryAdapter, navigationStore, useNavigation, useRouteParams };
//# sourceMappingURL=index.js.map
